import React, { useState, useEffect } from 'react';
import { inject, observer } from "mobx-react";
import AddIcCallIcon from '@mui/icons-material/AddIcCall';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnSharpIcon from '@mui/icons-material/LocationOnSharp';
// Translations
import _useTranslate from '../hooks/_useTranslate';
import { pickLanguageWord } from '../utils/pickLanguageWord.js';
import { DateTime, Info } from "luxon";
// Dedicated header styles
import './Header.css';

const Header = ({ feathersStore }) => {

  let footer = _useTranslate(feathersStore.language, "footer");

  const [settingsPhone, setSettingsPhone]   = useState("");
  const [openingTimeStr, setOpeningTimeStr] = useState("");
  const [closingHours, setClosingHours]     = useState("");
  const [holiday, setHoliday]               = useState(false);
  const [overnight, setOvernight]           = useState(false);

  useEffect(() => {

    const now           = DateTime.now().setZone(feathersStore.timezone());
    const luxonToday    = (now.weekday + 6) % 7;
    const luxonYesterday = (now.minus({ days: 1 }).startOf('day').weekday + 6) % 7;

    let dayData = feathersStore?.settings[
      `${Info.weekdays('long', { locale: 'en-US' })[luxonToday].toLowerCase()}`
    ];

    if (dayData) {
      const opening = dayData.openingHours;

      // Check for overnight overlap (opened yesterday, still open past midnight)
      const openingDt = now.set({
        hour:        +opening.split(':')[0],
        minute:      +opening.split(':')[1],
        seconds:     0,
        milliseconds: 0,
      });
      let overnightFlag = false;
      if (feathersStore.restaurantOpen && now < openingDt) {
        dayData = feathersStore?.settings[
          `${Info.weekdays('long', { locale: 'en-US' })[luxonYesterday].toLowerCase()}`
        ];
        overnightFlag = true;
      }

      setOpeningTimeStr(opening);
      setOvernight(overnightFlag);
      setClosingHours(dayData.closingHours);
      setHoliday(dayData.hoursEnabled);
    }

    const phoneNumber =
      feathersStore.settings?.phoneNumbers &&
      feathersStore.settings?.phoneNumbers?.find(p => p.numberEnabled)?.phoneNumber;
    setSettingsPhone(phoneNumber);

  }, [
    feathersStore?.settings?.phoneNumbers,
    feathersStore?.settings?.monday?.openingHours,
    feathersStore?.settings?.tuesday?.openingHours,
    feathersStore?.settings?.wednesday?.openingHours,
    feathersStore?.settings?.thursday?.openingHours,
    feathersStore?.settings?.friday?.openingHours,
    feathersStore?.settings?.saturday?.openingHours,
    feathersStore?.settings?.sunday?.openingHours,
    feathersStore.restaurantOpen,
    feathersStore.language,
  ]);

  const address = feathersStore._translateAddress();

  return (
    <div className="body-container">
      <header className="header-top-bar">

        {/* ── Phone ──────────────────────────────── */}
        {settingsPhone && (
        <div className="header-top-bar-el-upper">
          <span className="top-bar-el-first top-bar-el">
            <AddIcCallIcon />
            {/* tel: works on both desktop (opens dialler/Facetime) and mobile */}
            <a href={`tel:${settingsPhone}`} className="header-only-text-item">
              <span>{settingsPhone}</span>
            </a>
          </span>
        </div>
        )}

        {/* ── Location ───────────────────────────── */}
        {address && (
        <div className="header-top-bar-el-upper">
          <span className="top-bar-el-cent top-bar-el">
            <LocationOnSharpIcon />
            <a
              className="header-only-text-item"
              href={`https://www.google.com/maps/search/?api=1&query=${feathersStore.settings?.latitude}%2C${feathersStore.settings?.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {address}
            </a>
          </span>
        </div>
        )}

        {/* ── Opening hours ──────────────────────── */}
        {(holiday || openingTimeStr || closingHours) && (
        <div className="header-top-bar-el-upper">
          <span className="top-bar-el-last top-bar-el">
            <AccessTimeIcon />
            {holiday ? (
              <span className="open-hours header-only-text-item">
                {feathersStore.settings?.closedShopText
                  ? feathersStore._translateClosedShopText()
                  : footer.holidayText}
              </span>
            ) : (
              <span className="open-hours header-only-text-item">
                <span className="hours-label">
                  {pickLanguageWord(feathersStore.language, "openingTime")}{" "}
                  {pickLanguageWord(feathersStore.language, "delivery")}:{" "}
                </span>
                {overnight
                  ? `${pickLanguageWord(feathersStore.language, "to")} ${closingHours}`
                  : (openingTimeStr === "00:00" && closingHours === "23:59")
                    ? footer.allDay
                    : `${openingTimeStr} - ${closingHours}`}
              </span>
            )}
          </span>
        </div>
        )}

      </header>
    </div>
  );
};

export default inject('feathersStore')(observer(Header));
